'use client'

import Link from 'next/link'
import { useLocale } from 'next-intl'
import React from 'react'

import { DEFAULT_LOCALE } from '@/modules/languages/lib/catalog'
import { localizeInternalHref } from '@/modules/languages/lib/localizeInternalHref'

type LocaleLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string
  /** Props do `<Link>` do Next (só usadas no idioma padrão / soft-nav). */
  prefetch?: boolean
  scroll?: boolean
  replace?: boolean
}

/**
 * Link interno que HERDA o locale ativo.
 *
 * - **Href externo/âncora** → `<Link>` do Next com o href original (intocado).
 * - **Idioma padrão (pt)** → `<Link>` do Next (soft-nav) com o caminho INTERNO já normalizado
 *   (host próprio removido; sem prefixo, pois pt é o padrão).
 * - **Locale ≠ padrão (es/en)** → prefixa o caminho (`/es`,`/en`) e usa `<a>` (HARD NAV). Motivo:
 *   `/es/*` é REWRITE de middleware (não rota real do App Router) → a navegação soft do `<Link>` não
 *   troca o locale (mesma falha provada no seletor). O hard nav recarrega → o middleware serve a
 *   página localizada.
 *
 * A classificação interno×externo e a normalização (relativo, ABSOLUTO do próprio host, prefixo de
 * locale já colado) ficam centralizadas em `localizeInternalHref` — o MESMO cérebro usado no server —
 * então URL absoluta do nosso host (ex.: o editor cola `https://balizoassessoria.com.br/x`) passa a herdar
 * o locale, e link externo/afiliado permanece intocado. Sem risco de `/en/en` (o helper tira o prefixo
 * antes de reaplicar).
 *
 * Drop-in para `<Link href="…">` em links INTERNOS de conteúdo (cards, nav, breadcrumb, etc.).
 * NÃO usar em sitemaps/feeds/JSON-LD (esses devem ficar canônicos/absolutos).
 */
export const LocaleLink = React.forwardRef<HTMLAnchorElement, LocaleLinkProps>(function LocaleLink(
  { href, children, prefetch, scroll, replace, ...rest },
  ref,
) {
  const locale = useLocale()
  // `localized`: caminho interno normalizado E já prefixado p/ o locale ativo (ex.: '/en/x'); OU o href
  // externo/âncora cru (inalterado). No idioma padrão vem sem prefixo ('/x').
  const localized = localizeInternalHref(href, locale)
  const isInternal =
    typeof localized === 'string' && localized.startsWith('/') && !localized.startsWith('//')

  // Externo/afiliado/âncora → href original intocado, soft-nav do Next.
  if (!isInternal) {
    return (
      <Link href={href} ref={ref} prefetch={prefetch} scroll={scroll} replace={replace} {...rest}>
        {children}
      </Link>
    )
  }

  // Idioma padrão (pt): caminho sem prefixo → mantém SOFT-NAV + prefetch/scroll/replace.
  if (locale === DEFAULT_LOCALE) {
    return (
      <Link href={localized} ref={ref} prefetch={prefetch} scroll={scroll} replace={replace} {...rest}>
        {children}
      </Link>
    )
  }

  // Locale ≠ padrão (/en,/es…): rewrite de middleware → HARD NAV.
  return (
    <a href={localized} ref={ref} {...rest}>
      {children}
    </a>
  )
})
